import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';

// Cache voor auth state om onnodige API calls te voorkomen
const authCache = {
  state: null,
  userId: null,
  timestamp: 0,
  TTL: 30000, // 30 seconden cache
  
  isValid(userId) {
    return (
      this.state !== null &&
      this.userId === userId &&
      Date.now() - this.timestamp < this.TTL
    );
  },
  
  set(userId, state) {
    this.state = state;
    this.userId = userId;
    this.timestamp = Date.now();
  },
  
  clear() {
    this.state = null;
    this.userId = null;
    this.timestamp = 0;
  }
};

// Luister naar auth changes om cache te invalideren.
// TOKEN_REFRESHED wordt NIET gewist: een token refresh verandert de auth state niet,
// dus de cache mag stabiel blijven om onnodige loading-spinners te voorkomen.
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
    authCache.clear();
  }
});

const RequireAuth = ({ children }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [isFullyAuthenticated, setIsFullyAuthenticated] = useState(false);
  const checkInProgress = useRef(false);
  const abortController = useRef(null);

  const checkAuth = useCallback(async () => {
    // Voorkom dubbele checks
    if (checkInProgress.current) return;
    
    if (!user) {
      setChecking(false);
      setIsFullyAuthenticated(false);
      return;
    }

    // Check cache eerst
    if (authCache.isValid(user.id)) {
      setIsFullyAuthenticated(authCache.state);
      setChecking(false);
      return;
    }

    checkInProgress.current = true;
    abortController.current = new AbortController();

    try {
      // Parallelle API calls voor betere performance
      const [factorsResult, aalResult] = await Promise.all([
        supabase.auth.mfa.listFactors(),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      ]);

      const { data: factorsData, error: factorsError } = factorsResult;
      const { data: aalData, error: aalError } = aalResult;
      
      if (factorsError) {
        console.error('RequireAuth: Error checking factors:', factorsError);
        setIsFullyAuthenticated(false);
        authCache.set(user.id, false);
        return;
      }

      if (aalError) {
        console.error('RequireAuth: Error checking AAL:', aalError);
        setIsFullyAuthenticated(false);
        authCache.set(user.id, false);
        return;
      }

      const allFactors = [...(factorsData?.totp || []), ...(factorsData?.phone || [])];
      const hasVerifiedFactor = allFactors.some(factor => factor.status === 'verified');

      // Als geen 2FA ingesteld, moet gebruiker terug naar login voor setup
      if (!hasVerifiedFactor) {
        setIsFullyAuthenticated(false);
        authCache.set(user.id, false);
        return;
      }

      let authenticated = false;

      // Gebruiker moet aal2 hebben (2FA voltooid in deze sessie)
      if (aalData?.currentLevel === 'aal2') {
        authenticated = true;
      } else if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel === 'aal1') {
        authenticated = false;
      } else {
        // Geen 2FA vereist voor deze gebruiker
        authenticated = true;
      }

      setIsFullyAuthenticated(authenticated);
      authCache.set(user.id, authenticated);
      
    } catch (error) {
      console.error('RequireAuth: Unexpected error:', error);
      setIsFullyAuthenticated(false);
      authCache.set(user.id, false);
    } finally {
      setChecking(false);
      checkInProgress.current = false;
    }
  }, [user]);

  useEffect(() => {
    checkAuth();

    // Cleanup
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [checkAuth]);

  // Hervalideer auth op de achtergrond bij navigatie als de cache verlopen is.
  // Geen setChecking(true): de gebruiker ziet geen loading-spinner voor een achtergrondcheck.
  // Alleen als de check mislukt wordt de gebruiker doorgestuurd naar /login.
  useEffect(() => {
    if (user && !authCache.isValid(user.id)) {
      checkAuth();
    }
  }, [location.pathname, user, checkAuth]);

  if (checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!user || !isFullyAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default RequireAuth;
